import React, { useEffect, useMemo, useState } from 'react';
import { FiList, FiSearch, FiCheck } from 'react-icons/fi';
import Modal from '../../../components/Modal';

const RevokeAccessModal = ({
  isOpen,
  onClose,
  selectedAssessment,
  revokeShareDetails,
  revokeLoading,
  revokeSelectedUserIds,
  onRevoke,
  onToggleUser,
}) => {
  const norm = (s) => (s || '').trim().toLowerCase();

  const orgGroups = useMemo(
    () => revokeShareDetails?.organizations || [],
    [revokeShareDetails]
  );

  // Every recipient this assessment is currently shared with, flattened across the share's
  // organization groups. A recipient's `organization` is their own Keycloak org (their vendor).
  const allUsers = useMemo(() => orgGroups.flatMap((o) => o.users || []), [orgGroups]);

  // Names of the account(s) the assessment was shared UNDER (e.g. "ZenCo"). Recipients whose
  // OWN org matches one of these are "organization users"; everyone else is a "vendor user".
  // This mirrors exactly how the Share modal splits recipients (Organization list vs. Vendor
  // dropdown), so the same person lands under the same bucket in both places.
  const accountNames = useMemo(
    () => new Set(orgGroups.map((o) => norm(o.organizationName || ''))),
    [orgGroups]
  );

  // One tile per distinct recipient org, classified as Organization (matches the account) or
  // Vendor (a vendor org you shared to). Account org tiles are shown even with 0 recipients so
  // the admin always sees which organization the assessment was shared under.
  const tiles = useMemo(() => {
    const map = new Map(); // orgName -> users[]
    allUsers.forEach((u) => {
      const key = (u.organization || '').trim() || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    });
    // Ensure every account org appears as a tile even when no organization user was shared.
    orgGroups.forEach((o) => {
      const name = (o.organizationName || '').trim();
      if (name && !map.has(name)) map.set(name, []);
    });

    return Array.from(map.entries())
      .map(([name, users]) => ({
        key: name,
        name,
        kind: accountNames.has(norm(name)) ? 'Organization' : 'Vendor',
        users,
        count: users.length,
      }))
      // Organization tiles first, then vendors, each alphabetical.
      .sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === 'Organization' ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
  }, [orgGroups, allUsers, accountNames]);

  // Which tiles are active (used only to filter the list below).
  const [activeKeys, setActiveKeys] = useState(() => new Set());
  useEffect(() => {
    if (isOpen) setActiveKeys(new Set());
  }, [isOpen, selectedAssessment?.id]);

  const toggleTile = (key) =>
    setActiveKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // No tile active → show everyone (so a recipient is never hidden); otherwise show recipients
  // whose org belongs to an active tile (union).
  const visibleUsers =
    activeKeys.size === 0
      ? allUsers
      : allUsers.filter((u) => activeKeys.has((u.organization || '').trim() || 'Other'));

  const handleSelectAllVisible = () => {
    visibleUsers.forEach((u) => {
      if (!revokeSelectedUserIds.includes(u.userId)) onToggleUser(u.userId);
    });
  };
  const handleDeselectAllVisible = () => {
    visibleUsers.forEach((u) => {
      if (revokeSelectedUserIds.includes(u.userId)) onToggleUser(u.userId);
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Revoke Access"
      size="xl"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg text-sm transition-colors border-[#919191]/40 text-[#53545a] hover:bg-[#919191]/5"
          >
            Cancel
          </button>
          <button
            onClick={onRevoke}
            disabled={revokeLoading || !revokeShareDetails?.organizations?.length}
            className="px-4 py-2 bg-[#F47F2E] text-white rounded-lg text-sm font-medium hover:bg-[#e06a1f] transition-colors disabled:opacity-50"
          >
            {revokeSelectedUserIds.length > 0
              ? `Revoke Selected (${revokeSelectedUserIds.length})`
              : 'Revoke All'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Assessment context */}
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
          <FiList className="text-[#F47F2E] flex-shrink-0" />
          <span className="text-sm font-medium text-[#53545a]">
            {selectedAssessment?.assessmentDescription || `Assessment #${selectedAssessment?.id}`}
          </span>
        </div>

        {revokeLoading ? (
          <div className="flex items-center justify-center py-8">
            <FiSearch className="animate-spin text-[#0077b5] text-xl mr-2" />
            <span className="text-sm text-[#919191]">Loading share details...</span>
          </div>
        ) : allUsers.length > 0 ? (
          <>
            {/* Filter tiles — the Organization(s) the assessment was shared under, plus the
                Vendor(s) the recipients belong to. Click to filter the list; none active
                means "show all". */}
            <div>
              <h3 className="text-sm font-medium text-[#919191] mb-2">Organization &amp; Vendor</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {tiles.map((t) => {
                  const active = activeKeys.has(t.key);
                  const isOrg = t.kind === 'Organization';
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => toggleTile(t.key)}
                      className={`w-full text-left border transition-colors cursor-pointer ${
                        active
                          ? isOrg
                            ? 'border-[#0077b5] bg-[#0077b5]/15 hover:bg-[#0077b5]/20'
                            : 'border-[#F47F2E] bg-[#F47F2E]/10 hover:bg-[#F47F2E]/15'
                          : 'border-[#919191]/30 bg-white hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3 px-4 py-2.5 text-[#53545a]">
                        <div
                          className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            active
                              ? isOrg
                                ? 'bg-[#0077b5] border-[#0077b5]'
                                : 'bg-[#F47F2E] border-[#F47F2E]'
                              : 'border-[#919191]/50 bg-white'
                          }`}
                        >
                          {active && <FiCheck className="text-white text-xs" />}
                        </div>
                        <FiList className="text-[#F47F2E] text-base flex-shrink-0" />
                        <span className="text-sm font-medium truncate">{t.name}</span>
                        <span className="ml-auto flex items-center gap-2 flex-shrink-0">
                          <span className="text-[10px] uppercase tracking-wide text-[#919191]">
                            {t.kind}
                          </span>
                          <span className="text-xs text-[#919191]">
                            {t.count} user{t.count !== 1 ? 's' : ''}
                          </span>
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-[#919191]">
                Shared With
                {revokeSelectedUserIds.length > 0 && (
                  <span className="ml-2 text-[#0b79b8] font-semibold">
                    • {revokeSelectedUserIds.length} selected
                  </span>
                )}
              </h4>
              {visibleUsers.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSelectAllVisible}
                    className="text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors"
                  >
                    Select All
                  </button>
                  <span className="text-[#919191]/40">|</span>
                  <button
                    type="button"
                    onClick={handleDeselectAllVisible}
                    className="text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors"
                  >
                    Deselect All
                  </button>
                </div>
              )}
            </div>

            {visibleUsers.length === 0 ? (
              <div className="border rounded-lg border-slate-200 bg-slate-50 px-4 py-10 text-center">
                <p className="text-sm text-slate-500">No recipients for the selected filter.</p>
              </div>
            ) : (
              <div className="border rounded-lg border-slate-200">
                <div className="max-h-72 overflow-y-auto">
                  {visibleUsers.map((user) => {
                    const isUserSelected = revokeSelectedUserIds.includes(user.userId);
                    return (
                      <div
                        key={user.userId}
                        onClick={() => onToggleUser(user.userId)}
                        className={`flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 cursor-pointer transition-colors ${
                          isUserSelected
                            ? 'bg-[#F47F2E]/5 border-[#F47F2E]/20 text-[#53545a]'
                            : 'border-[#919191]/20 text-[#919191] hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          isUserSelected
                            ? 'bg-[#F47F2E] border-[#F47F2E]'
                            : 'border-[#919191]/50 bg-white'
                        }`}>
                          {isUserSelected && (
                            <FiCheck className="text-white text-xs" />
                          )}
                        </div>
                        <div className="w-7 h-7 rounded-full bg-[#F47F2E]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-[#F47F2E]">{user.userName?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#53545a] truncate">
                            {user.userName}
                            {user.role && (
                              <span className="ml-1 text-xs text-slate-400 italic">({user.role})</span>
                            )}
                          </p>
                          {user.email && <p className="text-xs text-[#919191] truncate">{user.email}</p>}
                          {user.organization && (
                            <p className="text-[11px] text-[#0b79b8] truncate">{user.organization}</p>
                          )}
                        </div>
                        <span className="text-xs text-[#919191] flex-shrink-0">{new Date(user.sharedAt).toLocaleDateString()}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-xs text-[#919191]">
              {revokeSelectedUserIds.length > 0
                ? `${revokeSelectedUserIds.length} user(s) selected — only these users will be revoked.`
                : 'No users selected — clicking "Revoke All" will remove access for everyone.'}
            </p>
          </>
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-[#919191]">No active shares found for this assessment.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RevokeAccessModal;
